import { Component, OnInit, EventEmitter, Input, Output, DestroyRef } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import * as _ from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { ISelectOption } from 'src/app/models/selectOptions';
import { ISelectOptionGroups } from 'src/app/models/selectOptions/selectOptionGroups.interface';

import { FilterTypesEnum } from 'src/app/models/filters/filterTypes.enum';
import { debounceTime, distinctUntilChanged, filter, finalize, switchMap, tap } from 'rxjs';
import { createDepartmentVerticalFilter } from 'src/app/common/departmentFilterUtils';
import { AiFindingsFiltersModel } from 'src/app/models/filters/implementations/pageFilters/aiFindingsFilters.model';
import { AiFindingsHttpService } from 'src/app/services/httpServices/detection/aiFindingsHttpService/ai-findings-http.service';
import { AutocompleteSearchResultsV2Model } from 'src/app/models/api/autocompleteSearchResultsV2.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LabelModel } from 'src/app/models/api/common';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { NONE_LABEL_OPTION } from 'src/app/models/filters/implementations/pageFilters/aiFindingsFilters.model';

const DEFAULT_DEBOUNCE = 1000;
const MIN_CHARS_FOR_FILTER = 3;

@Component({
  selector: 'app-ai-findings-vertical-filters',
  templateUrl: './ai-findings-vertical-filters.component.html',
  styleUrls: ['./ai-findings-vertical-filters.component.scss'],
})
export class AiFindingsVerticalFiltersComponent implements OnInit {
  @Input() public pageFilters: AiFindingsFiltersModel;
  @Input() public isFiltersMenuOpen: boolean;
  @Output() public filtersOnMenuStateChange = new EventEmitter<boolean>();
  @Output() public optionOnSelection = new EventEmitter<ISelectOption[]>();

  public optionsInsideCategories$: BehaviorSubject<ISelectOptionGroups[]> = new BehaviorSubject<ISelectOptionGroups[]>(
    []
  );
  public expandedCategories: { [optionGroup: string]: boolean } = {};
  public selectedIds: Set<string>;
  public isOpen = true;

  public selectedOptions: ISelectOption[] = [];

  public searchLabelNameValues: string[] = [];
  public allLabels: LabelModel[] = [];
  public searchLabelShown: ISelectOption[] = [];
  public searchLabelShownHeight = '0px';

  public searchEnvironmentNameValuesCtrl = new UntypedFormControl();
  public filteredEnvironmentNameValues: ISelectOption[] = [];
  public isEnvironmentNameLoading$ = new BehaviorSubject(false);
  public environmentNameFilterEnum = FilterTypesEnum.Environment;

  public searchActorValuesCtrl = new UntypedFormControl();
  public filteredActorValues: ISelectOption[] = [];
  public searchThreadIdValuesCtrl = new UntypedFormControl();
  public filteredThreadIdValues: ISelectOption[] = [];
  public isActorLoading$ = new BehaviorSubject(false);
  public isThreadIdLoading$ = new BehaviorSubject(false);
  public actorFilterEnum = FilterTypesEnum.Actor;
  public threadIdFilterEnum = FilterTypesEnum.ThreadId;

  public departmentFilter = createDepartmentVerticalFilter();

  public dynamicCategories: string[];

  constructor(
    private readonly _aiFindingsHttpService: AiFindingsHttpService,
    private readonly _destroyRef: DestroyRef,
    private readonly _store: StoreService
  ) {
    this._store.labels$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((labels: LabelModel[]) => {
      if (labels.length === 0) return;
      this._initLabelNameFilter(labels);
    });
  }

  ngOnInit(): void {
    this.dynamicCategories = this.pageFilters.getDynamicFilters();

    this.pageFilters
      .getAllFiltersOptions()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((options) => {
        const optionsInsideCategories: ISelectOptionGroups[] = [];
        const indexByCategory: { [key: string]: number } = {};

        options.forEach((option) => {
          if (option.categoryName) {
            if (option.categoryName in indexByCategory) {
              optionsInsideCategories[indexByCategory[option.categoryName]].options.push(option);
            } else if (!this.dynamicCategories.includes(option.categoryName)) {
              optionsInsideCategories.push({
                displayName: option.categoryName,
                options: [option],
                isDynamic: false,
                isNew: false,
                selectedCount: 0,
              });
              indexByCategory[option.categoryName] = optionsInsideCategories.length - 1;
            } else {
              optionsInsideCategories.push({
                displayName: option.categoryName,
                options: [option],
                isDynamic: true,
                isNew: false,
                selectedCount: 0,
              });
            }
          } else {
            optionsInsideCategories.push({
              options: [option],
            } as ISelectOptionGroups);
          }
        });

        this.dynamicCategories.forEach((category) => {
          optionsInsideCategories.push({
            displayName: category,
            options: [],
            isDynamic: true,
            isNew: false,
            selectedCount: 0,
          });
        });

        this.optionsInsideCategories$.next(optionsInsideCategories);
      });

    this.pageFilters.onUpdate$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      const newSelectedIds: Set<string> = new Set();

      this.pageFilters.getAllSelectedOptions().forEach((option) => {
        newSelectedIds.add(option.id);
      });
      this.selectedIds = newSelectedIds;
    });

    this._initActorFilter();
    this._initThreadIdFilter();
    this._initEnvironmentNameFilter();
  }

  public expandClicked(optionGroupDisplayName: string, status: boolean) {
    this.expandedCategories[optionGroupDisplayName] = status;
  }

  public onToggleClick() {
    this.filtersOnMenuStateChange.emit(!this.isFiltersMenuOpen);
    this.isOpen = !this.isOpen;
  }

  public tablesFiltersChange(selectedOptions: ISelectOption[]) {
    this.pageFilters.updateFilterFromUserSelection(undefined, selectedOptions);
  }

  public actorSubmitted(users: ISelectOption[] = []): void {
    this.pageFilters.actorFilters.updateOptions(_.union(this.pageFilters.actorFilters.getOptions$().value, users));
    this.pageFilters.updateFilterFromUserSelection(undefined, _.union(this._getSelectedOptions(), users));
  }

  public threadIdSubmitted(users: ISelectOption[] = []): void {
    this.pageFilters.actorFilters.updateOptions(_.union(this.pageFilters.actorFilters.getOptions$().value, users));
    this.pageFilters.updateFilterFromUserSelection(undefined, _.union(this._getSelectedOptions(), users));
  }

  public onSelect(id: string): void {
    if (!this.selectedIds.has(id)) {
      this.selectedIds.add(id);
    } else {
      this.selectedIds.delete(id);
    }
    this.optionOnSelection.emit(this._getSelectedOptions());
  }

  private _getSelectedOptions(): ISelectOption[] {
    const selectedIndexArray = Array.from(this.selectedIds);
    const optionsById = _.keyBy(this.pageFilters.getAllFiltersOptions().value, 'id');

    /// This is needed because there are options that can be selected but are not shown.
    const selectedOptions = selectedIndexArray.map((id: string) => optionsById[id]);

    return selectedOptions.filter((options) => typeof options !== 'undefined');
  }

  private _initFilter(
    control: UntypedFormControl,
    filter_name: string,
    filteredValues: ISelectOption[],
    loadingSubject: BehaviorSubject<boolean>,
    filterType: FilterTypesEnum
  ) {
    control.valueChanges
      .pipe(
        filter((res) => res !== null && res?.length >= MIN_CHARS_FOR_FILTER),
        distinctUntilChanged(),
        debounceTime(DEFAULT_DEBOUNCE),
        tap(() => {
          filteredValues.length = 0;
          loadingSubject.next(true);
        }),
        switchMap((value) =>
          this._aiFindingsHttpService.getAutocompleteOptions(value, filter_name).pipe(
            finalize(() => {
              loadingSubject.next(false);
            })
          )
        ),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((data: AutocompleteSearchResultsV2Model[]) => {
        const byEmail = new Map<string, AutocompleteSearchResultsV2Model>();

        for (const item of data) {
          if (!item.email) {
            // treat “no-email” items as unique
            byEmail.set(`__NOEMAIL__${item.id}`, item);
            continue;
          }

          const existing = byEmail.get(item.email);
          if (!existing) {
            byEmail.set(item.email, item); // first time we see this e-mail
          } else {
            /* if a later row has id≠email it replaces one whose id===email             *
             * (so the more informative row wins).  If both rows satisfy the same       *
             * condition we let the later one win, adjust if you prefer “first wins”.   */
            const preferred = item.id !== item.email && existing.id === existing.email ? item : existing;
            byEmail.set(item.email, preferred);
          }
        }
        for (const chosen of byEmail.values()) {
          filteredValues.push({
            id: chosen.id,
            value: chosen.id,
            displayName: chosen.resultText,
            filterType: filterType,
            categoryName: filterType,
            withoutBeautifyDisplayName: true,
          } as ISelectOption);
        }
      });
  }

  private _initActorFilter() {
    this._initFilter(
      this.searchActorValuesCtrl,
      'actor',
      this.filteredActorValues,
      this.isActorLoading$,
      FilterTypesEnum.Actor
    );
  }

  private _initThreadIdFilter() {
    this.searchThreadIdValuesCtrl.valueChanges
      .pipe(
        filter((res) => res !== null && res?.length >= 3),
        distinctUntilChanged(),
        tap(() => {
          this.filteredThreadIdValues = [];
        }),
        tap((value) => {
          this.filteredThreadIdValues.push({
            id: value,
            value: value,
            displayName: value,
            filterType: FilterTypesEnum.ThreadId,
            categoryName: FilterTypesEnum.ThreadId,
            withoutBeautifyDisplayName: true,
          } as ISelectOption);
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  private _initEnvironmentNameFilter() {
    this._initFilter(
      this.searchEnvironmentNameValuesCtrl,
      'environment',
      this.filteredEnvironmentNameValues,
      this.isEnvironmentNameLoading$,
      FilterTypesEnum.Environment
    );
  }

  public environmentNameSubmitted(environments: ISelectOption[] = []): void {
    this.pageFilters.environmentFilters.updateOptions(
      _.union(this.pageFilters.environmentFilters.getOptions$().value, environments)
    );
    this.pageFilters.updateFilterFromUserSelection(undefined, _.union(this._getSelectedOptions(), environments));
  }

  public labelNameSubmitted(labels: string[] = []): void {
    const labelOptions = labels.flatMap((label: string) => {
      const labelModel = this.allLabels.find((labelExist: LabelModel) => labelExist.displayName === label);
      return labelModel
        ? [
            {
              id: labelModel.id,
              value: labelModel.displayName,
              displayName: labelModel.displayName,
              filterType: FilterTypesEnum.Label,
              categoryName: FilterTypesEnum.Label,
            } as ISelectOption,
          ]
        : [];
    });

    this.searchLabelShown = [NONE_LABEL_OPTION, ...labelOptions];

    this.searchLabelShownHeight = this._getSearchLabelShownHeight();
  }

  private _initLabelNameFilter(labels: LabelModel[]) {
    labels.forEach((label: LabelModel) => {
      if (this.allLabels.find((labelExist: LabelModel) => labelExist.id === label.id)) return;
      this.allLabels.push(label);
      this.searchLabelNameValues.push(label.displayName);
    });
    this.labelNameSubmitted(this.searchLabelNameValues);
  }

  private _getSearchLabelShownHeight(): string {
    const height = Math.min(this.searchLabelShown.length * 30, 210);
    return height + 'px';
  }
}
